import React, { useMemo, useState } from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Users, Search, ChevronDown, ChevronUp, CheckSquare, Square } from 'lucide-react';

const ROLE_GROUPS = [
  { key: 'comerciais', label: 'Comerciais', roles: ['comercial', 'chefe_equipa'] },
  { key: 'angariadores', label: 'Angariadores', roles: ['angariador'] },
  { key: 'gestao', label: 'Gestão (admin / parceiro / backoffice)', roles: ['admin', 'parceiro', 'backoffice'] },
  { key: 'super', label: 'Super Admin / Gestor', roles: ['super_admin', 'gestor'] },
];

/**
 * Multi-select recipient picker:
 * - Partners: checkboxes (empty = all)
 * - Role groups: checkboxes (empty = all)
 * - Filtered users: checkboxes with select-all/clear
 *
 * Props:
 *   partners: [{ id, name }]
 *   users: [{ id, name, email, role, partner_id }]
 *   roleFilter: string[] (allowed roles for current sender, [] = all)
 *   selectedUserIds: Set<string>
 *   onChange(setOfUserIds)
 *   accent: 'rose' | 'emerald' (color theme)
 *   loading: bool
 */
export const MultiRecipientPicker = ({
  partners,
  users,
  roleFilter = [],
  selectedUserIds,
  onChange,
  accent = 'rose',
  loading = false,
}) => {
  const [selectedPartners, setSelectedPartners] = useState(new Set());
  const [selectedRoles, setSelectedRoles] = useState(new Set());
  const [partnersOpen, setPartnersOpen] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(true);
  const [search, setSearch] = useState('');

  const visibleRoleGroups = useMemo(() => {
    if (!roleFilter.length) return ROLE_GROUPS;
    return ROLE_GROUPS.filter(g => g.roles.some(r => roleFilter.includes(r)));
  }, [roleFilter]);

  const filteredUsers = useMemo(() => {
    let arr = users;
    if (selectedPartners.size > 0) {
      arr = arr.filter(u => selectedPartners.has(u.partner_id || '__none__'));
    }
    if (selectedRoles.size > 0) {
      const allowedRoles = new Set();
      selectedRoles.forEach(rg => {
        const g = ROLE_GROUPS.find(x => x.key === rg);
        if (g) g.roles.forEach(r => allowedRoles.add(r));
      });
      arr = arr.filter(u => allowedRoles.has(u.role));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(u => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
    }
    return arr;
  }, [users, selectedPartners, selectedRoles, search]);

  const togglePartner = (id) => {
    const next = new Set(selectedPartners);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedPartners(next);
    // Reset user selections that no longer match
    const matchingIds = new Set(users.filter(u => next.size === 0 || next.has(u.partner_id || '__none__')).map(u => u.id));
    const filteredSel = new Set([...selectedUserIds].filter(uid => matchingIds.has(uid)));
    onChange(filteredSel);
  };

  const toggleRole = (key) => {
    const next = new Set(selectedRoles);
    next.has(key) ? next.delete(key) : next.add(key);
    setSelectedRoles(next);
  };

  const toggleUser = (id) => {
    const next = new Set(selectedUserIds);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(next);
  };

  const selectAllFilteredUsers = () => {
    const next = new Set(selectedUserIds);
    filteredUsers.forEach(u => next.add(u.id));
    onChange(next);
  };

  const clearUsers = () => onChange(new Set());

  const accentClasses = accent === 'emerald'
    ? { border: 'border-emerald-300', bg: 'bg-emerald-50', text: 'text-emerald-700', check: 'text-emerald-600' }
    : { border: 'border-rose-300', bg: 'bg-rose-50', text: 'text-rose-700', check: 'text-rose-600' };

  return (
    <div className="space-y-3" data-testid="multi-recipient-picker">
      {/* Partners */}
      <div className="border border-slate-200 rounded-lg">
        <button
          type="button"
          onClick={() => setPartnersOpen(o => !o)}
          className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 rounded-lg"
          data-testid="partners-toggle"
        >
          <span className="text-sm font-medium text-slate-700">
            Parceiros {selectedPartners.size === 0 ? <span className="text-slate-400 font-normal">(todos)</span> : <span className={`${accentClasses.text} font-semibold`}>({selectedPartners.size} selecionados)</span>}
          </span>
          {partnersOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {partnersOpen && (
          <div className="px-3 pb-3 space-y-1 max-h-40 overflow-y-auto" data-testid="partners-list">
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={() => setSelectedPartners(new Set([...partners.map(p => p.id), '__none__']))} className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="partners-select-all">Selecionar todos</button>
              <span className="text-slate-300">·</span>
              <button type="button" onClick={() => setSelectedPartners(new Set())} className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="partners-clear">Limpar</button>
            </div>
            <label className="flex items-center gap-2 px-1 py-1 hover:bg-slate-50 rounded cursor-pointer">
              <input type="checkbox" checked={selectedPartners.has('__none__')} onChange={() => togglePartner('__none__')} className={`accent-current ${accentClasses.check}`} />
              <span className="text-xs text-slate-500 italic">(sem parceiro)</span>
            </label>
            {partners.map(p => (
              <label key={p.id} className="flex items-center gap-2 px-1 py-1 hover:bg-slate-50 rounded cursor-pointer" data-testid={`partner-${p.id}`}>
                <input type="checkbox" checked={selectedPartners.has(p.id)} onChange={() => togglePartner(p.id)} className={`accent-current ${accentClasses.check}`} />
                <span className="text-xs text-slate-700">{p.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Role groups */}
      {visibleRoleGroups.length > 1 && (
        <div className="border border-slate-200 rounded-lg">
          <button
            type="button"
            onClick={() => setRolesOpen(o => !o)}
            className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 rounded-lg"
            data-testid="roles-toggle"
          >
            <span className="text-sm font-medium text-slate-700">
              Categoria {selectedRoles.size === 0 ? <span className="text-slate-400 font-normal">(todas)</span> : <span className={`${accentClasses.text} font-semibold`}>({selectedRoles.size} selecionadas)</span>}
            </span>
            {rolesOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
          {rolesOpen && (
            <div className="px-3 pb-3 space-y-1" data-testid="roles-list">
              {visibleRoleGroups.map(g => (
                <label key={g.key} className="flex items-center gap-2 px-1 py-1 hover:bg-slate-50 rounded cursor-pointer" data-testid={`role-${g.key}`}>
                  <input type="checkbox" checked={selectedRoles.has(g.key)} onChange={() => toggleRole(g.key)} className={`accent-current ${accentClasses.check}`} />
                  <span className="text-xs text-slate-700">{g.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Users */}
      <div className="border border-slate-200 rounded-lg">
        <button
          type="button"
          onClick={() => setUsersOpen(o => !o)}
          className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 rounded-lg"
          data-testid="users-toggle"
        >
          <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <Users className="w-4 h-4" /> Destinatários
            {selectedUserIds.size === 0 ? (
              <span className="text-slate-400 font-normal">(nenhum)</span>
            ) : (
              <span className={`${accentClasses.text} font-semibold`}>{selectedUserIds.size} de {filteredUsers.length}</span>
            )}
          </span>
          {usersOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {usersOpen && (
          <div className="px-3 pb-3 space-y-2" data-testid="users-list">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar por nome ou email..." className="pl-9 text-xs h-8" data-testid="users-search" />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={selectAllFilteredUsers} className={`text-xs ${accentClasses.text} hover:underline flex items-center gap-1`} data-testid="users-select-all">
                <CheckSquare className="w-3.5 h-3.5" /> Selecionar todos visíveis ({filteredUsers.length})
              </button>
              <span className="text-slate-300">·</span>
              <button type="button" onClick={clearUsers} className="text-xs text-slate-500 hover:text-slate-700 hover:underline flex items-center gap-1" data-testid="users-clear">
                <Square className="w-3.5 h-3.5" /> Limpar
              </button>
            </div>
            <div className="max-h-52 overflow-y-auto border border-slate-100 rounded">
              {loading ? (
                <p className="text-xs text-slate-400 text-center py-3">A carregar...</p>
              ) : filteredUsers.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-3">Sem utilizadores neste filtro</p>
              ) : (
                filteredUsers.map(u => {
                  const isSelected = selectedUserIds.has(u.id);
                  return (
                    <label
                      key={u.id}
                      className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer transition-colors ${isSelected ? accentClasses.bg : 'hover:bg-slate-50'}`}
                      data-testid={`user-${u.id}`}
                    >
                      <input type="checkbox" checked={isSelected} onChange={() => toggleUser(u.id)} className={`accent-current ${accentClasses.check}`} />
                      <span className="text-xs flex-1">
                        <span className="font-medium text-slate-800">{u.name}</span>
                        <span className="text-slate-400"> · {u.role} · {u.email}</span>
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiRecipientPicker;
